import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, Shield, Users, Check, Phone, Wallet, Mail, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { toast } from 'sonner';

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_stake-blc/artifacts/qx16sddk_Screenshot__247_-removebg-preview%20(1).png";

const COUNTRY_CODES = [
  { code: '+1', name: 'USA' },
  { code: '+44', name: 'UK' },
  { code: '+92', name: 'Pakistan' },
  { code: '+91', name: 'India' },
  { code: '+86', name: 'China' },
  { code: '+971', name: 'UAE' },
  { code: '+966', name: 'Saudi Arabia' },
];

export default function SignupPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1); // 1: referral, 2: form, 3: verification
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referrerName, setReferrerName] = useState('');
  const [referralVerified, setReferralVerified] = useState(false);
  const [verifyingReferral, setVerifyingReferral] = useState(false);
  
  // Email verification state
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeResendTimer, setCodeResendTimer] = useState(0);
  const codeInputRefs = useRef([]);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    countryCode: '+92',
    wallet_address: '',
    password: '',
    confirmPassword: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('ref')) {
      verifyReferral();
    }
  }, []);

  // Countdown timer for resend code
  useEffect(() => {
    if (codeResendTimer > 0) {
      const timer = setTimeout(() => setCodeResendTimer(codeResendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeResendTimer]);

  const verifyReferral = async () => {
    if (!referralCode) {
      toast.error('Please enter a referral code');
      return;
    }

    setVerifyingReferral(true);
    try {
      const response = await authApi.validateReferral(referralCode);
      setReferrerName(response.data.referrer_name);
      setReferralVerified(true);
      setStep(2);
      toast.success('Referral verified!');
    } catch (error) {
      toast.error('Invalid referral code');
      setReferralVerified(false);
    } finally {
      setVerifyingReferral(false);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleSliderStart = (e) => {
    e.preventDefault();
    const slider = sliderRef.current;
    if (!slider) return;

    const startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const sliderRect = slider.parentElement.getBoundingClientRect();
    const maxX = sliderRect.width - 44;

    const handleMove = (moveEvent) => {
      const currentX = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const deltaX = currentX - startX;
      const newPos = Math.max(0, Math.min(deltaX, maxX));
      setSliderPosition(newPos);

      if (newPos >= maxX - 10) {
        setCaptchaVerified(true);
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      }
    };

    const handleEnd = () => {
      if (!captchaVerified) {
        setSliderPosition(0);
      }
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);
  };

  // Send verification code to email
  const sendVerificationCode = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setSendingCode(true);
    try {
      await authApi.sendVerification(formData.email, formData.full_name);
      toast.success('Verification code sent to your email!');
      setStep(3);
      setCodeResendTimer(60); // 60 second cooldown
      setVerificationCode(['', '', '', '', '', '']);
      // Focus first input
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1); // Only last character
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setVerificationCode(newCode);
      codeInputRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  // Verify code and complete registration
  const verifyCodeAndRegister = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setVerifyingCode(true);
    try {
      // First verify the code
      await authApi.verifyCode(formData.email, code, 'signup');
      
      // Then register the user
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: `${formData.countryCode}${formData.phone}`,
        wallet_address: formData.wallet_address,
        referral_code: referralCode,
      });
      
      toast.success('Registration successful! Welcome to STAKE BLC!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingCode(false);
    }
  };

  // Handle form submission (Step 2 -> Step 3)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error('Please verify the captcha');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!agreedTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (!formData.wallet_address.startsWith('0x')) {
      toast.error('Please enter a valid BEP-20 wallet address');
      return;
    }

    // Send verification code instead of registering directly
    await sendVerificationCode();
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-400 flex items-center justify-center relative overflow-hidden py-12">
      {/* Background */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.1)_0%,_transparent_50%)] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[480px] px-6">
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center mb-8"
        >
          <div className="relative w-48 h-32 flex items-center justify-center mb-2">
            <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-2xl animate-pulse" />
            <img src={LOGO_MAIN} alt="STAKE BLC" className="h-28 relative z-10 object-contain" />
          </div>
        </motion.div>

        {/* Signup Form */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-2xl p-8 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
          
          <div className="text-center mb-6">
            <h2 className="text-xl text-white font-normal">Join Our Community</h2>
            <p className="text-xs text-zinc-500 mt-1">Start your journey to financial freedom</p>
          </div>

          {/* Step 1: Referral Verification */}
          {step === 1 && (
            <div className="mb-4">
              <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 relative overflow-hidden">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-medium text-amber-100">Referral Required</span>
                </div>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Enter Referral Code" 
                    className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-amber-500/50 outline-none transition-colors"
                    data-testid="referral-code-input"
                  />
                  <button 
                    onClick={verifyReferral}
                    disabled={verifyingReferral}
                    className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500 hover:text-black text-xs font-medium transition-all border border-amber-500/20 disabled:opacity-50"
                    data-testid="verify-referral-btn"
                  >
                    {verifyingReferral ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
                {referralVerified && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 text-xs text-green-400">
                    <Check className="w-4 h-4" />
                    Invited by: <span className="font-medium text-white">{referrerName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Referral Info */}
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2 text-xs text-green-400 mb-4">
                <Check className="w-4 h-4" />
                Invited by: <span className="font-medium text-white">{referrerName}</span>
              </div>

              {/* Full Name */}
              <div className="space-y-1">
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <User className="w-4 h-4 text-zinc-500" />
                  <input 
                    type="text" 
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    placeholder="Full Name" 
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="signup-fullname"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1">
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  <input 
                    type="email" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Email Address" 
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="signup-email"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-1">
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  <select 
                    value={formData.countryCode}
                    onChange={(e) => setFormData({...formData, countryCode: e.target.value})}
                    className="bg-transparent border-none outline-none text-sm text-white"
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code} className="bg-zinc-900">{c.code}</option>
                    ))}
                  </select>
                  <input 
                    type="tel" 
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Phone Number" 
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="signup-phone"
                    required
                  />
                </div>
              </div>

              {/* Wallet Address */}
              <div className="space-y-1">
                <label className="text-[10px] text-amber-500 uppercase tracking-widest ml-1 font-medium">
                  BLC Wallet Address (BEP-20)
                </label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2 border-amber-500/20 bg-amber-500/5">
                  <Wallet className="w-4 h-4 text-amber-400" />
                  <input 
                    type="text" 
                    value={formData.wallet_address}
                    onChange={(e) => setFormData({...formData, wallet_address: e.target.value})}
                    placeholder="0x..." 
                    className="bg-transparent border-none outline-none text-sm text-white w-full font-mono placeholder-zinc-600"
                    data-testid="signup-wallet"
                    required
                  />
                  {formData.wallet_address.startsWith('0x') && formData.wallet_address.length > 10 && (
                    <Check className="w-4 h-4 text-green-400" />
                  )}
                </div>
                <p className="text-[10px] text-zinc-500 ml-1">Must be a valid Binance Smart Chain address.</p>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16Z"/>
                    <circle cx="12" cy="16" r="2"/>
                    <path strokeLinecap="round" d="M6 10V8a6 6 0 1 1 12 0v2"/>
                  </svg>
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handlePasswordChange}
                    placeholder="Create Password" 
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="signup-password"
                    required
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Strength Bar */}
                <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden flex">
                  <div 
                    className={`h-full transition-all duration-300 ${
                      passwordStrength <= 25 ? 'bg-red-500' : 
                      passwordStrength <= 50 ? 'bg-orange-500' : 
                      passwordStrength <= 75 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${passwordStrength}%` }}
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1">
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16Z"/>
                    <circle cx="12" cy="16" r="2"/>
                    <path strokeLinecap="round" d="M6 10V8a6 6 0 1 1 12 0v2"/>
                  </svg>
                  <input 
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    placeholder="Confirm Password" 
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="signup-confirm-password"
                    required
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-zinc-500 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-[10px] text-red-400 ml-1">Passwords do not match</p>
                )}
              </div>

              {/* Slider Captcha */}
              <div className="space-y-1.5 pt-2">
                <div className="slider-captcha-container relative" data-testid="signup-captcha">
                  {!captchaVerified && (
                    <div className="slider-text">SLIDE TO VERIFY</div>
                  )}
                  <div 
                    className="slider-track" 
                    style={{ width: captchaVerified ? '100%' : `${sliderPosition + 44}px` }}
                  />
                  <div 
                    ref={sliderRef}
                    className={`slider-handle ${captchaVerified ? 'verified' : ''}`}
                    style={{ left: captchaVerified ? 0 : `${sliderPosition}px`, width: captchaVerified ? '100%' : '44px' }}
                    onMouseDown={!captchaVerified ? handleSliderStart : undefined}
                    onTouchStart={!captchaVerified ? handleSliderStart : undefined}
                  >
                    {captchaVerified ? (
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Shield className="w-4 h-4" /> Verified
                      </span>
                    ) : (
                      <ArrowRight className="w-5 h-5" />
                    )}
                  </div>
                </div>
              </div>

              {/* Terms */}
              <label className="flex items-start gap-3 cursor-pointer group custom-checkbox">
                <input 
                  type="checkbox" 
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="hidden"
                  data-testid="signup-terms"
                />
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors mt-0.5 ${
                  agreedTerms ? 'bg-amber-500 border-amber-500' : 'border-zinc-600'
                }`}>
                  {agreedTerms && <Check className="w-3 h-3 text-black" />}
                </div>
                <span className="text-[10px] text-zinc-400 group-hover:text-zinc-300">
                  I agree to the <a href="#" className="text-amber-500 hover:underline">Terms of Service</a> & <a href="#" className="text-amber-500 hover:underline">Privacy Policy</a>.
                </span>
              </label>

              {/* Submit */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-3 rounded-lg gold-btn shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="signup-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          )}

          {/* Step 3: Email Verification */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Back Button */}
              <button 
                onClick={() => setStep(2)}
                className="text-xs text-zinc-500 hover:text-white flex items-center gap-1 transition-colors"
              >
                <ArrowRight className="w-3 h-3 rotate-180" /> Back to form
              </button>

              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg text-white font-medium">Verify Your Email</h3>
                <p className="text-xs text-zinc-500 mt-2">
                  We've sent a 6-digit code to<br/>
                  <span className="text-amber-400">{formData.email}</span>
                </p>
              </div>

              {/* Code Input */}
              <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => codeInputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="w-11 h-14 text-center text-xl font-bold bg-black/30 border-2 border-white/10 rounded-lg text-white focus:border-amber-500/50 focus:outline-none transition-colors"
                    data-testid={`verify-code-${index}`}
                  />
                ))}
              </div>

              {/* Verify Button */}
              <button
                onClick={verifyCodeAndRegister}
                disabled={verifyingCode || verificationCode.join('').length !== 6}
                className="w-full py-3 rounded-lg gold-btn shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="verify-code-submit"
              >
                {verifyingCode ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Verify & Create Account
                  </>
                )}
              </button>

              {/* Resend Code */}
              <div className="text-center">
                {codeResendTimer > 0 ? (
                  <p className="text-xs text-zinc-500">
                    Resend code in <span className="text-amber-400">{codeResendTimer}s</span>
                  </p>
                ) : (
                  <button
                    onClick={sendVerificationCode}
                    disabled={sendingCode}
                    className="text-xs text-amber-400 hover:text-amber-300 font-medium transition-colors disabled:opacity-50"
                  >
                    {sendingCode ? 'Sending...' : "Didn't receive code? Resend"}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-zinc-500">
              Already a member? {' '}
              <Link to="/login" className="text-amber-400 hover:text-amber-300 font-medium transition-colors" data-testid="login-link">
                Sign In
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
